// src/api/client.ts - COMPLETE PRODUCTION-READY VERSION
import axios from 'axios';
import { setupTokenRefresh } from './tokenRefresh';

// Determine API URL based on environment
const getApiUrl = () => {
  // Production on custom domain carryngo.ng
  if (window.location.hostname === 'carryngo.ng' || 
      window.location.hostname === 'www.carryngo.ng') {
    console.log('📡 Running on production domain, using API subdomain');
    return 'https://api.carryngo.ng';
  }
  
  // Render.com preview deployment
  if (window.location.hostname.includes('onrender.com')) {
    console.log('📡 Running on Render preview, using backend URL');
    return 'https://carryngo-backend.onrender.com';
  }
  
  // Local development
  console.log('📡 Running on localhost, using local API');
  return import.meta.env.VITE_API_URL || 'http://localhost:5000';
};

export const API_BASE_URL = getApiUrl();
console.log('✅ API_BASE_URL configured:', API_BASE_URL);

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000,
  withCredentials: false, // JWT is handled via Authorization header
});

// Silent endpoints that shouldn't show errors to users
const SILENT_ENDPOINTS = [
  '/api/notifications/sse',
  '/api/chat/poll',
  '/api/orders/available',
  '/api/orders/my-orders',
  '/api/health',
  '/api/p2p/listings',
  '/api/marketplace/listings',
  '/api/quick-offers',
  '/api/notifications',
  '/api/users/',
  '/api/dashboard/',
  '/api/orders/status'
];

// Track background operations
const backgroundOperations = new Set<string>();

// Request interceptor for auth token and operation tracking
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Track background operations (polling, etc.)
  if (config.url?.includes('/poll') || config.url?.includes('/sse') || 
      config.url?.includes('/available') || config.url?.includes('/my-orders') ||
      config.url?.includes('/status')) {
    const key = `${config.method}-${config.url}`;
    backgroundOperations.add(key);
  }
  
  return config;
}, (error) => {
  console.error('Request interceptor error:', error);
  return Promise.reject(error);
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Clean up background operation tracking
    if (response.config.url) {
      const key = `${response.config.method}-${response.config.url}`;
      backgroundOperations.delete(key);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';
    const method = originalRequest?.method || '';
    const isSilent = SILENT_ENDPOINTS.some(endpoint => url.includes(endpoint));
    const isBackground = backgroundOperations.has(`${method}-${url}`);
    
    const shouldShowToUser = !isSilent && !isBackground && error.response?.status !== 401;
    
    if (import.meta.env.DEV) {
      if (!isSilent && !isBackground) {
        console.error('API Error:', {
          status: error.response?.status,
          message: error.response?.data?.message || error.message,
          url: originalRequest?.url,
          method: originalRequest?.method,
          data: error.response?.data
        });
      } else {
        console.log(`📡 Background API: ${method} ${url} - ${error.response?.status || 'error'}`);
      }
    }

    // Handle 401 Unauthorized - Token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Attempt to refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh-token`, {
            refreshToken
          });
          
          if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Clear auth data and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        
        const currentPath = window.location.pathname;
        if (!currentPath.includes('/login') && !currentPath.includes('/signup') && !currentPath.includes('/forgot-password')) {
          window.location.href = '/login?session=expired';
        }
      }
    }

    // Return silent response for background operations
    if (isSilent || isBackground) {
      return Promise.resolve({
        data: {
          success: false,
          silent: true,
          message: 'Background operation failed (silent)',
          _silent: true
        },
        status: error.response?.status || 500,
        statusText: error.response?.statusText || 'Error',
        headers: error.response?.headers,
        config: originalRequest,
      });
    }

    // Return user-friendly error messages
    if (error.response?.data?.message) {
      return Promise.reject(error);
    }

    // Network errors
    if (error.code === 'ECONNABORTED') {
      error.response = {
        ...error.response,
        data: {
          message: 'Request timeout. Please check your internet connection.',
          code: 'TIMEOUT_ERROR'
        }
      };
    } else if (!error.response) {
      error.response = {
        ...error.response,
        data: {
          message: 'Network error. Please check your internet connection.',
          code: 'NETWORK_ERROR'
        }
      };
    }

    return Promise.reject(error);
  }
);

// Token refresh setup
setupTokenRefresh(api);

export default api;